import { EyeOutlined, PlusOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useTable } from '@refinedev/antd'
import { Breadcrumb, Button, Input, Table } from 'antd'
import { useCallback, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'

type BasicUserGroupDto = components['schemas']['BasicUserGroupDto']

export default function UserGroupsListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlSearch = searchParams.get('q') || ''
  const [search, setSearch] = useState(urlSearch)
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>()

  const syncSearchToUrl = useCallback(
    (value: string) => {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            if (value) {
              next.set('q', value)
            } else {
              next.delete('q')
            }
            return next
          },
          { replace: true },
        )
      }, 300)
    },
    [setSearchParams],
  )

  const { tableProps } = useTable<BasicUserGroupDto>({
    resource: 'user-groups',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
  })

  // Client-side name filter
  const filteredData = search
    ? tableProps.dataSource?.filter(
        (g) =>
          g.name?.toLowerCase().includes(search.toLowerCase()) ||
          g.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : tableProps.dataSource

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: 'User Groups' }]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/user-groups/create')}
        >
          Create Group
        </Button>
      </div>

      <Input.Search
        placeholder="Search groups..."
        style={{ marginBottom: 16, maxWidth: 400 }}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value)
          syncSearchToUrl(e.target.value)
        }}
        allowClear
      />

      <Table
        {...tableProps}
        pagination={tablePaginationConfig(tableProps)}
        dataSource={filteredData}
        rowKey="name"
        onRow={(record) => ({
          style: { cursor: 'pointer' },
          onClick: () => navigate(`/user-groups/${record.name}`),
        })}
        columns={[
          { title: 'Name', dataIndex: 'name', key: 'name' },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (d: string) => d || '—',
          },
          {
            title: '',
            key: 'actions',
            width: 80,
            render: (_: unknown, record: BasicUserGroupDto) => (
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/user-groups/${record.name}`)}
              />
            ),
          },
        ]}
      />
    </div>
  )
}
